/* eslint-disable @typescript-eslint/no-explicit-any */

import { get } from "lodash";
import isEqual from "lodash/isEqual";
import moment, { MomentFormatSpecification, MomentInput } from "moment";
import { User } from "../northstar/User";

export * from "./DataConverter/DataConverter";
export * from "./DateTime/DateTime";
export * from "./DistanceCalculator/calculateMilesBetweenPoints";
export * from "./NameFormatter/nameExtraction";
export * from "./NumberFormatter/NumberFormatter";
export * from "./PhoneNumber/parsePhoneNumber";
export * from "./RevenueThreshold/RevenueThreshold";
export * from "./TransactionTypeConverter/TransactionTypeConverter";

export type DeepPartial<T> = T extends Array<infer U>
    ? DeepPartial<U>[]
    : T extends Map<infer K, infer V>
    ? Map<DeepPartial<K>, DeepPartial<V>>
    : T extends Set<infer M>
    ? Set<DeepPartial<M>>
    : // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;
export type Mutable<T> = { -readonly [key in keyof T]: T[key] };
export type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];
export type Await<T> = T extends {
    then(onfulfilled?: (value: infer U) => unknown): unknown;
}
    ? U
    : T;
export type PickAndPartial<T, K extends keyof T> = Pick<T, K> & Partial<T>;
export type PickAndAtLeastOneOther<T, K extends keyof T> = Pick<T, K> & AtLeastOne<Omit<T, K>>;
export type Intersection<A, B> = {
    [P in keyof A & keyof B]: A[P] | B[P];
};
export type ObjectOf<K extends string | number | symbol, T> = { [P in K]: T };

export type Primitive = string | number | bigint | boolean | undefined | symbol;

export type NonPathable = Buffer | Date;

export type Path<T, Prefix = "", PrevTypes = never> = {
    [K in keyof T]: T extends PrevTypes
        ? ""
        : T[K] extends (Primitive | NonPathable) | Array<any>
        ? `${string & Prefix}${string & K}`
        :
              | `${string & Prefix}${string & K}`
              | Path<T[K], `${string & Prefix}${string & K}.`, T extends Primitive | NonPathable ? PrevTypes : T | PrevTypes>;
}[keyof T];

export type PathValue<T, P extends Path<T>> = P extends `${infer Prev}.${infer Rest}`
    ? PathValue<
          // @ts-expect-error Not sure how to force our types here. We should be able to assume Prev is a key of T due to the definition of Path
          T[Prev],
          // @ts-expect-error Not sure how to force our types here. We should be able to assume Rest is a Path of T[Prev]
          Rest
      >
    : // @ts-expect-error Not sure how to force our types here. We should be able to assume P is a key of T
      T[P];

/** Extracts params out of a url string
 * @example type ParamKeys = ExtractParamKeys<"/users/:userId/permissions">; // "userId"
 * @example type ParamKeys = ExtractParamKeys<"/companies/:companyId/drivers/:driverId/details">; // "companyId"|"driverId"
 */
export type ExtractParamKeys<S extends string, Arr extends string = never> = S extends `${string}:${infer Rest}`
    ? Rest extends `${infer Var}/${infer Postfix}`
        ? ExtractParamKeys<Postfix, Var | Arr>
        : Rest | Arr
    : Arr;
export type ExtractParams<S extends string> = Record<ExtractParamKeys<S>, string | number>;
export type Unpacked<T> = T extends (infer U)[] ? U : T;

export function getChanges<T>(updated: T, original: T): Partial<T> {
    return Object.fromEntries(
        Object.entries(updated).filter((changes) => {
            return !isEqual(original[changes[0]], changes[1]);
        })
    ) as Partial<T>;
}

export function pick<T, K extends (keyof T)[]>(obj: T, keys: K): Pick<T, K[number]> {
    return Object.fromEntries(keys.map((key) => [key, obj[key]])) as Pick<T, K[number]>;
}

export function addCommas(str: string): string {
    let s = str.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    while ((s[0] === "0" && s.length > 1) || s[0] === ",") {
        s = s.substring(1);
    }
    return s;
}

export function formatNumber(value = 0, type = "", decimalPlaces = 0): string {
    let string = "";
    if (value !== null) {
        string = value.toString();
    }
    let valueStr = "0";
    let decimals = "";
    if (string.length) {
        const decimalIndex = string.indexOf(".");
        decimals =
            decimalPlaces > 0
                ? decimalIndex > -1
                    ? string.substring(decimalIndex, decimalIndex + decimalPlaces + 1).padEnd(decimalPlaces + 1, "0")
                    : "." + Array(decimalPlaces).fill("0").join("")
                : "";

        // parse it into a float and remove the symbols if necessary
        const value = parseInput(string, true);

        valueStr = String(value).split(".")[0];
    }

    switch (type) {
        case "$":
            return `$${addCommas(valueStr)}${decimals}`;
        case "%":
            return `${addCommas(valueStr)}${decimals}%`;
        case "":
            return `${addCommas(valueStr)}${decimals}`;
        default:
            return `${addCommas(valueStr)}${decimals}`;
    }
}

export function formatPhone(str: string): string {
    if (!str) {
        return "";
    } else if (str.length === 11) {
        return `+${str[0]} (${str.substring(1, 4)}) ${str.substring(4, 7)}-${str.substring(7, 11)}`;
    } else if (str.length === 10) {
        return `(${str.substring(0, 3)}) ${str.substring(3, 6)}-${str.substring(6, 10)}`;
    } else if (str.length === 7) {
        return `${str.substring(0, 3)}-${str.substring(3, 7)}`;
    } else {
        return str;
    }
}

export function parseInput(str: string, float = false): number {
    if (!str) return 0;

    const s = str.replace(/[^\d.KkMm]/g, "");
    let n = 0;
    if (s.search(/[Mm]/g) > -1) {
        const split = s.replace(/[Kk]/g, "").split(/[Mm]/);
        n = parseFloat(split[0]) * 1000000;
        if (float === false) {
            n = Math.round(n);
        }
    } else if (s.search(/[Kk]/g) > -1) {
        const split = s.replace(/[Mm]/g, "").split(/[Kk]/);
        n = parseFloat(split[0]) * 1000;
        if (float === false) {
            n = Math.round(n);
        }
    } else {
        n = float ? parseFloat(s) : parseInt(s);
    }
    if (isNaN(n)) return 0;
    return n;
}

export function utf8ArrayToStr(array: Array<number>): string {
    let out, i, c;
    let char2, char3;

    out = "";
    const len = array.length;
    i = 0;
    while (i < len) {
        c = array[i++];
        switch (c >> 4) {
            case 0:
            case 1:
            case 2:
            case 3:
            case 4:
            case 5:
            case 6:
            case 7:
                // 0xxxxxxx
                out += String.fromCharCode(c);
                break;
            case 12:
            case 13:
                // 110x xxxx   10xx xxxx
                char2 = array[i++];
                out += String.fromCharCode(((c & 0x1f) << 6) | (char2 & 0x3f));
                break;
            case 14:
                // 1110 xxxx  10xx xxxx  10xx xxxx
                char2 = array[i++];
                char3 = array[i++];
                out += String.fromCharCode(((c & 0x0f) << 12) | ((char2 & 0x3f) << 6) | ((char3 & 0x3f) << 0));
                break;
            default:
            //pass
        }
    }

    return out;
}

export function mask(string: string, mask: string): string {
    if (!string) return "";
    const arr = Array.from(string);
    let result = "";
    for (let i = 0; i < mask.length; i++) {
        if (!arr.length) break;
        if (mask[i] === "?") {
            result += arr.shift();
        } else {
            result += mask[i];
        }
    }
    return result;
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary_string = atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
}

export function getDomain(email: string): string | undefined {
    return (email || "").match(/.*@(.*)/)?.[1].toLowerCase();
}

export function humanizeTime(time: MomentInput, format?: MomentFormatSpecification): string {
    const m = moment(time, format);
    return m.isSameOrBefore(moment().add(-20, "hours"))
        ? m.year() === moment().year()
            ? m.format("MMM D [at] h:mm A")
            : m.format("MMM D, YYYY [at] h:mm A")
        : m.fromNow();
}

export function wait(time: number): Promise<void> {
    return new Promise((res) => {
        setTimeout(res, time);
    });
}

export function getOWAEditLink(messageId: string): string {
    return `https://outlook.office365.com/mail/deeplink/compose/${window.encodeURIComponent(messageId)}?ItemID=${messageId}&exvsurl=1`;
}

export function getEnvironment(): "local" | "canary" | "production" {
    // eslint-disable-next-line no-restricted-globals
    const host = self.location.host;
    if (host.includes("localhost") || host.includes("127.0.0.1")) return "local";
    if (host.includes("canary")) return "canary";
    return "production";
}

/**
 * Returns a function to be used in a `sort` method. The function will sort based on the arguments provided
 */
export function toSortByFn<A>(order: SortByIterator<A>[]): (a: A, b: A) => number {
    return (a, b) => {
        for (const [orderKey, dir] of order) {
            const dirValue = dir === "ASC" ? -1 : 1;
            const valueA = (typeof orderKey === "string" ? get(a, orderKey) : (orderKey as ValueGetter<A>)(a)) as string | number;
            const valueB = (typeof orderKey === "string" ? get(b, orderKey) : (orderKey as ValueGetter<A>)(b)) as string | number;

            if (valueA < valueB) return dirValue;
            if (valueA > valueB) return -dirValue;
        }
        return 0;
    };
}

type ValueGetter<A> = (value: A) => unknown;
export type SortByIterator<A> = [Path<A> | ValueGetter<A>, "DESC" | "ASC"];
export function sortBy<A>(list: A[], order: SortByIterator<A>[], reverse?: boolean): A[] {
    const fn = toSortByFn(order);
    const result = list.slice().sort(fn);
    if (reverse) return result.reverse();
    return result;
}

export function getUserGroup(user: User): "AEG" | "AMG" | "ESG" {
    if (user.isDeveloper) return "ESG";
    if (user.isAgent) return "AEG";
    if (user.isManager || user.isAma) return "AMG";
    return "ESG";
}

export function isValidRegex(str: string): boolean {
    try {
        new RegExp(str);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Gets a date in X business days. Does not account for holidays.
 */
export function getDateInXBusinessDays(x: number, startDate?: moment.MomentInput) {
    const date = moment(startDate);
    const nonWorkDays = Math.floor(x / 5) * 2 + (date.day() + (x % 5) > 5 ? 2 : 0);
    return date.add(x + nonWorkDays, "days");
}

/**
 * Simple utility for stringifying a street address. This does not handle cases
 * where `street1`, `city`, `state`, or `zip` are missing.
 */
export function addressToString(address: { street?: string; street1?: string; street2: string; city: string; state: string; zip: string }) {
    let string = "";

    string += address.street || address.street1;

    if (address.street2) {
        string += `, ${address.street2}`;
    }

    string += `, ${address.city}, ${address.state} ${address.zip}`;

    return string;
}

/** Get the luminance of a hex color. This is useful for dynamically changing
 * font colors based on background colors in order to keep good contrast
 */
export function hexToLuma(hexColor: string): number {
    const hex = hexColor.replace(/#/, "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return [0.299 * r, 0.587 * g, 0.114 * b].reduce((a, b) => a + b) / 255;
}

/**
 * Distribute the given array into multiple smaller arrays based on the filters provided. Conditions are checked in the order
 * provided on a first-come-first-serve basis (i.e. if a value passes multiple filters the first to come in the filters array will be matched)
 *
 * @example distribute([1,2,3,5,8,13,21,30], [x => x < 10, x => x >= 10]) // [ [1,2,3,5,8], [13,21,30] ]
 */
export function distribute<T>(data: T[], filters: ((t: T) => boolean)[]) {
    const results: T[][] = filters.map(() => []);
    for (const row of data) {
        for (let i = 0; i < filters.length; i++) {
            if (filters[i](row)) {
                results[i].push(row);
            }
        }
    }
    return results;
}

export function parseFileName(name: string): [name: string, ext: string] {
    const match = name.match(/(.*)(\.[a-zA-Z0-9]+)/);
    if (match) {
        return [match[1], match[2]];
    } else {
        return [name, ""];
    }
}

export function capitalizeFirst(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function isWholeNumber(num: number, depth = 0): boolean {
    if (Number.isInteger(num)) return true;
    if (depth > 20) return false;

    const multiplied = num * 10;
    if (Number.isInteger(multiplied)) {
        return multiplied % 10 === 0;
    }

    return isWholeNumber(multiplied, depth + 1);
}

export function pluralize(value: number, base: string, plural: "es" | "s"): string {
    return value !== 1 ? convertYToI(base, plural) + plural : base;
}

function convertYToI(base: string, plural: "es" | "s"): string {
    if (plural === "s") return base;
    if (base.charAt(base.length - 1) !== "y") return base;
    return base.slice(0, -1) + "i";
}
